/**
 * Object Pool System for performance optimization
 * Reuses objects instead of constantly creating/destroying them
 */
export class ObjectPool {
    constructor() {
        this.pools = new Map();
    }
    
    /**
     * Create a new pool for a specific object type
     */
    createPool(type, createFn, resetFn, initialSize = 50) {
        const pool = {
            objects: [],
            createFn,
            resetFn,
            activeCount: 0
        };
        
        // Pre-populate the pool
        for (let i = 0; i < initialSize; i++) {
            pool.objects.push(createFn());
        }
        
        this.pools.set(type, pool);
    }
    
    /**
     * Get an object from the pool
     */
    get(type, ...args) {
        const pool = this.pools.get(type);
        if (!pool) {
            console.warn(`Pool ${type} not found`);
            return null;
        }
        
        let obj;
        if (pool.objects.length > 0) {
            obj = pool.objects.pop();
            pool.resetFn(obj, ...args);
        } else {
            obj = pool.createFn(...args);
        }
        
        pool.activeCount++;
        return obj;
    }
    
    /**
     * Return an object to the pool
     */
    release(type, obj) {
        const pool = this.pools.get(type);
        if (!pool) {
            console.warn(`Pool ${type} not found`);
            return;
        }
        
        if (obj.destroy) {
            obj.destroy();
        }
        
        pool.objects.push(obj);
        pool.activeCount = Math.max(0, pool.activeCount - 1);
    }
    
    /**
     * Get pool statistics for debugging
     */
    getStats() {
        const stats = {};
        for (const [type, pool] of this.pools) {
            stats[type] = {
                available: pool.objects.length,
                active: pool.activeCount,
                total: pool.objects.length + pool.activeCount
            };
        }
        return stats;
    }
    
    /**
     * Clear all pools (for cleanup)
     */
    clear() {
        for (const [type, pool] of this.pools) {
            pool.objects.forEach(obj => {
                if (obj.destroy) obj.destroy();
            });
            pool.objects.length = 0;
            pool.activeCount = 0;
        }
    }
}

// Global instance
export const objectPool = new ObjectPool(); 